import React, { useEffect, useState } from 'react';

import './index.less';

import IbButton from '../../../../components/common/IbButton';
import IbModal, { IbModalPosition } from '../../../../components/common/IbModal';
import IbTypography from '../../../../components/common/IbTypography';
import IbInput from '../../../../components/common/IbInput';
import { CheckFieldValueFnMap, useValidationForm } from '../../../../utils/validationUtil';
import { ValidationErrors } from '../../../../types';
import { FolderCreationRequest, FolderModel, FolderUpdatingRequest } from '../../../../../gamma-api';
import { getTranslateFunc } from '../../../../utils/stringUtil';

import { CONTENT_CLASS_NAME, EMPTY_FOLDER, FORM_ITEM_CLASS_NAME, MAIN_CLASS_NAME } from './const';

type FolderForm = {
  name: string;
};

const checkFieldValueFnMap: CheckFieldValueFnMap<FolderForm> = {
  name: (value: string) => !!value.length,
};

interface IFolderModalProps {
  visible: boolean;
  editFolder?: FolderModel;
  onCreate: (folder: FolderCreationRequest, onSubmitErrors: (errors: ValidationErrors) => void) => Promise<void>;
  onUpdate: (folder: FolderUpdatingRequest, onSubmitErrors: (errors: ValidationErrors) => void) => Promise<void>;
  onCancel: () => void;
}

const FolderModal: React.FC<IFolderModalProps> = ({ visible, editFolder, onCreate, onUpdate, onCancel }) => {
  const [saving, setSaving] = useState(false);

  const [folder, setFolder] = useState<FolderModel>(EMPTY_FOLDER);

  const translate = getTranslateFunc('Folder: Modal:');

  const initialFormValues: FolderForm = {
    name: editFolder?.name ?? '',
  };

  const {
    validationState,
    onFieldChange,
    onFieldBlur,
    onSubmitErrors,
    validate,
    reset,
    getShowError,
    getErrorMessage,
  } = useValidationForm<FolderForm>(initialFormValues, checkFieldValueFnMap);

  const onVisiblePropChange = () => {
    if (!visible) return;

    setSaving(false);
    setFolder(editFolder ?? EMPTY_FOLDER);
    reset();
  };
  useEffect(onVisiblePropChange, [visible]);

  const onNameChange = (value: string) => {
    setFolder({ ...folder, name: value });
    onFieldChange('name', value);
  };

  const onNameBlur = () => onFieldBlur('name');

  const onCreateButtonClick = async () => {
    if (!validate()) return;

    setSaving(true);
    await onCreate({ name: folder.name, settings: EMPTY_FOLDER.settings }, onSubmitErrors);
    setSaving(false);
  };

  const onUpdateButtonClick = async () => {
    if (!editFolder || !validate()) return;

    setSaving(true);
    await onUpdate({ name: folder.name, settings: EMPTY_FOLDER.settings }, onSubmitErrors);
    setSaving(false);
  };

  const renderContent = () => {
    return (
      <div className={CONTENT_CLASS_NAME}>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph type="secondary">{translate('Name: Title')}</IbTypography.Paragraph>
          <IbInput
            status={getShowError(validationState.name) ? 'error' : 'default'}
            value={folder.name}
            onBlur={onNameBlur}
            onChange={onNameChange}
          />
          {getShowError(validationState.name) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.name, translate('Name: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
      </div>
    );
  };

  const renderFooter = () => {
    return (
      <>
        {editFolder ? (
          <IbButton type="primary" onClick={onUpdateButtonClick}>
            {translate('Button: Update')}
          </IbButton>
        ) : (
          <IbButton type="primary" onClick={onCreateButtonClick}>
            {translate('Button: Create')}
          </IbButton>
        )}
        <IbButton type="secondary" onClick={onCancel}>
          {translate('Button: Cancel')}
        </IbButton>
      </>
    );
  };

  const title = editFolder ? translate('Title: Update') : translate('Title: Create');

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={renderFooter()}
      loading={saving}
      position={IbModalPosition.Centered}
      title={title}
      visible={visible}
      onCancel={onCancel}
    >
      {renderContent()}
    </IbModal>
  );
};

export default FolderModal;
